import React from 'react';
import { useAppConfigContext } from '../../../../contexts/AppConfigProvider';
import { IApiConfig, useApi } from '../../../../hooks/useApi';
import { useUser } from '../../../../hooks/useUser';
import {
    AuditLogEntry,
    AuditLogResponse,
} from '../identityProviderManagement.types';

export interface UseAuditLogResult {
    entries: AuditLogEntry[];
    loadFirstPage: () => void;
    /** Whether the request was sent: not while another read is in flight. */
    loadMore: () => boolean;
    hasMore: boolean;
    /** Loaded, and genuinely empty — distinct from "not loaded yet". */
    isEmpty: boolean;
    /** The first page has not arrived yet, and has not failed. Later reloads
     * keep the rows already shown, so they do not count. */
    isLoading: boolean;
    /** The last first-page read failed. Rows from an earlier read are kept. */
    loadFailed: boolean;
    /** A Show more is in flight. */
    isLoadingMore: boolean;
    /** The last Show more failed. The rows already shown are kept. */
    loadMoreFailed: boolean;
}

/**
 * Cursor-paged read of the authentication audit log. Paging follows
 * `nextCursor` until it is absent -- never infer the end from a short page,
 * because the service may return fewer entries than `limit` and still have
 * more. Read-only by design:
 * entries are written server-side as a side effect of adding, deleting or
 * triggering a migration, so no client can act without leaving a trace.
 */
export const useAuditLog = (): UseAuditLogResult => {
    const { api } = useAppConfigContext();
    const user = useUser();
    const tenantId = user?.tenantIdentifier;

    const [entries, setEntries] = React.useState<AuditLogEntry[]>([]);
    const [nextCursor, setNextCursor] = React.useState<number>();
    const [loaded, setLoaded] = React.useState(false);
    const [loadFailed, setLoadFailed] = React.useState(false);
    const [isLoadingMore, setLoadingMore] = React.useState(false);
    const [loadMoreFailed, setLoadMoreFailed] = React.useState(false);

    const url = `${api?.authProv}/v1/data/tenants/${tenantId}/identityProviders/auditLog`;

    const config = React.useMemo<IApiConfig>(
        () => ({
            url,
            method: 'GET',
            useAuth: true,
            executeType: 'manual',
            useCache: false,
        }),
        [url],
    );
    const { data, error, execute } = useApi<AuditLogResponse>(config);

    const inFlight = React.useRef<'firstPage' | 'more'>();

    React.useEffect(() => {
        if (!data) return;
        const appending = inFlight.current === 'more';
        inFlight.current = undefined;
        setEntries(previous =>
            appending
                ? [...previous, ...(data.entries ?? [])]
                : (data.entries ?? []),
        );
        setNextCursor(data.nextCursor);
        setLoaded(true);
        setLoadFailed(failed => appending && failed);
        setLoadMoreFailed(false);
        setLoadingMore(false);
    }, [data]);

    React.useEffect(() => {
        if (!error) return;
        const firstPage = inFlight.current === 'firstPage';
        const more = inFlight.current === 'more';
        inFlight.current = undefined;
        setLoadFailed(failed => failed || firstPage);
        setLoadMoreFailed(more);
        setLoadingMore(false);
    }, [error]);

    const loadFirstPage = React.useCallback(() => {
        inFlight.current = 'firstPage';
        setLoadFailed(false);
        // A refresh cancels a Show more still in flight.
        setLoadingMore(false);
        setLoadMoreFailed(false);
        execute({ url });
    }, [execute, url]);

    const loadMore = React.useCallback(() => {
        if (nextCursor == null || inFlight.current) return false;
        inFlight.current = 'more';
        setLoadingMore(true);
        setLoadMoreFailed(false);
        execute({ url: `${url}?cursor=${nextCursor}` });
        return true;
    }, [execute, nextCursor, url]);

    return {
        entries,
        loadFirstPage,
        loadMore,
        hasMore: nextCursor != null,
        isEmpty: loaded && entries.length === 0,
        isLoading: !loaded && !loadFailed,
        loadFailed,
        isLoadingMore,
        loadMoreFailed,
    };
};
